/**
 * Shared resolver for looking up a template definition (and its property schema)
 * from the DB-backed registry (window.alpineBlocksLayouts) by template id/name.
 *
 * Saved content references templates via data-template-id, but legacy content can
 * carry drifted ids with a doubled prefix (e.g. "johnny-mailer-johnny-mailer-2col-images"
 * for the DB layoutId "johnny-mailer-2col-images"), so matching falls back through
 * de-duplicated variants and finally the template name.
 */

const DOUBLED_PREFIX = /^(.+?)-\1-/;

/**
 * Find a template definition in the registry.
 * @param {string|null} templateId - data-template-id from content (may be stale/doubled)
 * @param {string|null} templateName - data-template-name from content
 * @param {Array|null} layouts - registry to search (defaults to window.alpineBlocksLayouts)
 * @returns {Object|null} The template/layout object, or null if not found
 */
export function resolveTemplate(templateId, templateName, layouts = window.alpineBlocksLayouts) {
    if (!Array.isArray(layouts) || layouts.length === 0) return null;

    if (templateId) {
        const id = String(templateId);

        // 1. Exact match on layoutId or id
        let template = layouts.find(t => t.layoutId === id || String(t.id) === id);
        if (template) return template;

        // 2. Content id has a doubled prefix — de-dup it and match
        const deduped = id.replace(DOUBLED_PREFIX, '$1-');
        if (deduped !== id) {
            template = layouts.find(t => t.layoutId === deduped || String(t.id) === deduped);
            if (template) return template;
        }

        // 3. Registry id has the doubled prefix instead (reverse direction)
        template = layouts.find(t =>
            typeof t.layoutId === 'string' && t.layoutId.replace(DOUBLED_PREFIX, '$1-') === id
        );
        if (template) return template;
    }

    // 4. Fall back to a case-insensitive name match
    if (templateName) {
        const name = String(templateName).toLowerCase();
        const template = layouts.find(t => t.name && String(t.name).toLowerCase() === name);
        if (template) return template;
    }

    return null;
}

/**
 * Resolve just the property schema for a template.
 * @returns {Array|null} The properties array, or null if unavailable/empty
 */
export function resolveTemplateSchema(templateId, templateName, layouts = window.alpineBlocksLayouts) {
    const template = resolveTemplate(templateId, templateName, layouts);
    const props = template?._properties || template?.properties;
    return Array.isArray(props) && props.length > 0 ? props : null;
}
